<template>
    <div class="login-section">
        <div class="login-content">
            <div class="login-content__holder">
                <AppAuthLogo class="registration-steps__logo"/>

                <!-- MAIN -->
                <div v-if="activeView === views.main">
                    <div class="section-title">
                        <h2 class="font-weight-normal">Log In To {{ appDisplayName }}</h2>
                    </div>
                    <div class="registration-form">
                        <form @submit.prevent="checkEmail">
                            <input
                                v-model.trim="form.email"
                                @input="onEmailInput"
                                type="email"
                                name="email"
                                autofocus
                                class="registration-form__text-field"
                                placeholder="Your email">
                            <span v-if="$v.form.email.$error" class="registration__error-message">
                                Whoops, make sure you've entered a valid email address!
                            </span>

                            <span v-else-if="emailFieldError" class="registration__error-message">
                                {{ emailFieldError.message }}
                                <router-link v-if="emailFieldError.code === 201"
                                             :to="{ name: 'register', params: {email: form.email} }">
                                    Register instead
                                </router-link>
                            </span>

                            <button type="submit"
                                    class="registration-form__submit btn-violet"
                                    :disabled="isLoading || !form.email">
                                <span v-show="isLoading">
                                    <i class="fas fa-spinner fa-pulse"></i>
                                </span>
                                <span v-show="!isLoading">
                                    Next
                                </span>
                            </button>

                            <span class="registration-form__submit-note">
                                Don't have an account yet?
                                <router-link :to="{ name: 'register', params: {email: this.form.email} }">
                                    Sign up
                                </router-link>
                            </span>
                        </form>
                    </div>
                    <div class="login-content__divider">OR</div>
                    <div class="login-content__sign-in sign-google">
                        <a @click.prevent="googleLogin" href="#" class="sign-in-link">
                            <div class="sign-in__icon">
                                <img src="google-logo.svg" alt="Google logo" width="20" height="20">
                            </div>
                            <div class="sign-in__text">Sign in with Google</div>
                        </a>
                    </div>
                    <div class="row">
                        <div class="col-md-6 col-12 ">

                            <span data-label="Sign in with Xero"
                                  class="w-100 xero-sso xero-sso-inline xero-sso-normal xero-sso-light xero-sso-font-family"
                                  style="background-color: transparent !important;">
                                <a @click.prevent="xeroLogin" href="#" class="xero-sso-link">
                                    <img
                                        src="data:image/svg+xml,%3Csvg viewBox='0 0 45 46' xmlns='http://www.w3.org/2000/svg'%3E %3Ctitle%3EXero%3C/title%3E %3Cpath fill='%2313B5EA' d='M22.457 45.49c12.402 0 22.456-10.072 22.456-22.495C44.913 10.57 34.86.5 22.457.5 10.054.5 0 10.57 0 22.995 0 35.418 10.054 45.49 22.457 45.49' /%3E %3Cpath fill='%23FFFFFF' d='M10.75 22.935l3.832-3.85a.688.688 0 0 0-.977-.965l-3.83 3.833-3.845-3.84a.687.687 0 0 0-.966.979l3.832 3.837-3.83 3.84a.688.688 0 1 0 .964.981l3.84-3.842 3.825 3.827a.685.685 0 0 0 1.184-.473.68.68 0 0 0-.2-.485l-3.83-3.846m22.782.003c0 .69.56 1.25 1.25 1.25a1.25 1.25 0 0 0-.001-2.5c-.687 0-1.246.56-1.246 1.25m-2.368 0c0-1.995 1.62-3.62 3.614-3.62 1.99 0 3.613 1.625 3.613 3.62s-1.622 3.62-3.613 3.62a3.62 3.62 0 0 1-3.614-3.62m-1.422 0c0 2.78 2.26 5.044 5.036 5.044s5.036-2.262 5.036-5.043c0-2.78-2.26-5.044-5.036-5.044a5.046 5.046 0 0 0-5.036 5.044m-.357-4.958h-.21c-.635 0-1.247.2-1.758.595a.696.696 0 0 0-.674-.54.68.68 0 0 0-.68.684l.002 8.495a.687.687 0 0 0 1.372-.002v-5.224c0-1.74.16-2.444 1.648-2.63.14-.017.288-.014.29-.014.406-.015.696-.296.696-.675a.69.69 0 0 0-.69-.688m-13.182 4.127c0-.02.002-.04.003-.058a3.637 3.637 0 0 1 7.065.055H16.2zm8.473-.13c-.296-1.403-1.063-2.556-2.23-3.296a5.064 5.064 0 0 0-5.61.15 5.098 5.098 0 0 0-1.973 5.357 5.08 5.08 0 0 0 4.274 3.767c.608.074 1.2.04 1.81-.12a4.965 4.965 0 0 0 1.506-.644c.487-.313.894-.727 1.29-1.222.006-.01.014-.017.022-.027.274-.34.223-.826-.077-1.056-.254-.195-.68-.274-1.014.156-.072.104-.153.21-.24.315-.267.295-.598.58-.994.802-.506.27-1.08.423-1.69.427-1.998-.023-3.066-1.42-3.447-2.416a3.716 3.716 0 0 1-.153-.58l-.01-.105h7.17c.982-.022 1.51-.717 1.364-1.51z' /%3E %3C/svg%3E"
                                        alt="Xero logo" aria-role="presentation" class="xero-sso-logo">

                                    <span class="xero-sso-label">Sign in with Xero</span>
                                </a>
                            </span>

                        </div>
                    </div>

                </div>

                <!-- PASSWORD -->
                <div v-if="activeView === views.password">
                    <div class="section-title">
                        <h2>Enter Your Password</h2>
                    </div>
                    <div class="registration-form">
                        <form @submit.prevent="login">
                            <input
                                v-model="form.password"
                                @input="onPasswordInput"
                                ref="password"
                                type="password"
                                class="registration-form__text-field"
                                placeholder="Your password">
                            <span v-if="$v.form.password.$error" class="registration__error-message">
                                Password field is required
                            </span>
                            <span v-else-if="passwordFieldError" class="registration__error-message">
                                {{ passwordFieldError }}
                            </span>

                            <button type="submit"
                                    class="registration-form__submit btn-violet"
                                    :disabled="isLoading || !form.password">
                                <span v-show="isLoading">
                                    <i class="fas fa-spinner fa-pulse"></i>
                                </span>
                                <span v-show="!isLoading">
                                    Log in
                                </span>
                            </button>

                            <span class="registration-form__submit-note">
                                <router-link :to="{ name: 'forgot', params: {email: this.form.email} }">
                                    I forgot my password
                                </router-link>
                            </span>
                            <a @click.prevent="activeView = views.main" class="registration-form__btn-back">
                                <i class="fas fa-chevron-left"></i>
                                Back
                            </a>
                        </form>
                    </div>
                </div>

                <!-- TWO FACTOR -->
                <div v-if="activeView === views.twoFactor">
                    <div class="section-title">
                        <h2>Enter your Two-factor code</h2>
                    </div>
                    <div class="registration-form">
                        <form @submit.prevent="twoFactorLogin">
                            <TwoFactorCode @input="onTwoFactorCodeInput"/>

                            <span v-if="twoFactorCodeFieldError" class="registration__error-message">
                                {{ twoFactorCodeFieldError }}
                            </span>
                            <!-- It seems this button is redundant -->
                            <button type="submit"
                                    class="registration-form__submit btn-violet mt-2"
                                    :disabled="isLoading || code2fa.length !== 6">
                                <span v-show="isLoading">
                                    <i class="fas fa-spinner fa-pulse"></i>
                                </span>
                                <span v-show="!isLoading">
                                    Next
                                </span>
                            </button>

                            <a @click.prevent="activeView = views.main" class="registration-form__btn-back">
                                <i class="fas fa-chevron-left"></i>
                                Back
                            </a>
                        </form>
                    </div>
                </div>
            </div>

            <PoweredByZudello v-if="isWebExpensesApp" class="mt-3 text-center"/>
        </div>

        <AuthAside/>

    </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {email, requiredIf} from 'vuelidate/lib/validators'
import {AUTH_REQUEST} from '../../store/mutations'
import TwoFactorCode from '../../components/TwoFactorCode'
import {lookupEmail} from '../../command/lookup-email'
import AuthAside from './parts/AuthAside'
import PoweredByZudello from '@/components/PoweredByZudello'
import {Env} from '../../../env'
import AppAuthLogo from '@/views/auth/AppAuthLogo'
import {get} from 'lodash'

export default {
    name: 'LoginPage',
    components: {AppAuthLogo, PoweredByZudello, AuthAside, TwoFactorCode},
    props: {
        email: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            touchMap: new WeakMap(),
            views: {
                main: 'MAIN',
                password: 'PASSWORD',
                twoFactor: 'TWO_FACTOR'
            },
            activeView: 'MAIN',

            form: {
                email: this.email,
                password: '',
                platform: 'WEBSITE',
                inviteCode: ''
            },
            code2fa: '',
            isLoading: false,

            emailFieldError: null,
            passwordFieldError: null,
            twoFactorCodeFieldError: null
        }
    },
    validations: {
        form: {
            email: {
                required: requiredIf(function() {
                    return this.activeView === this.views.main
                }),
                email
            },
            password: {
                required: requiredIf(function() {
                    return this.activeView === this.views.password
                })
            }
        }
    },
    computed: {
        ...mapGetters(['getInviteCode'])
    },
    methods: {
        checkEmail() {
            if (this.$v.$invalid) {
                this.$v.$touch()
                return
            }

            this.emailFieldError = null
            this.isLoading = true
            lookupEmail(this.form.email, false)
                .then(res => {
                    if (get(res, 'data.sso_required') &&
                        get(res, 'data.sso_url')) {
                        window.location.href = res.data.sso_url
                        return
                    }
                    if (res.error.message) {
                        this.emailFieldError = res.error
                    } else {
                        this.$v.$reset()
                        this.activeView = this.views.password
                        history.pushState({tabId: this.activeView}, null, this.$route.path)
                        this.$nextTick(() => {
                            this.$refs.password.focus()
                        })
                    }
                    this.isLoading = false
                })
        },

        onEmailInput() {
            this.emailFieldError = null
            this.vuelidateDelayTouch(this.$v.form.email, this.touchMap)
        },

        onTwoFactorCodeInput(code) {
            if (code && this.twoFactorCodeFieldError) {
                this.twoFactorCodeFieldError = null
            }
            this.code2fa = code
        },

        xeroLogin() {
            window.location.href = `${Env.authUrl}/auth/xero/redirect`
        },

        googleLogin() {
            window.location.href = `${Env.authUrl}/auth/google/redirect`
        },

        login() {
            if (this.$v.$invalid) {
                this.$v.$touch()
                return
            }

            this.isLoading = true
            this.passwordFieldError = null
            this.$store.dispatch(AUTH_REQUEST, this.form)
                .then(() => {
                    window.location = '/' // used instead push to avoid affecting of the external page css
                    this.isLoading = false
                })
                .catch(err => {
                    const errors = _.get(err, 'response.data.errors')
                    if (errors && errors.hasOwnProperty('password')) {
                        this.passwordFieldError = errors.password
                    }

                    if (_.get(err, 'response.status') === 423) {
                        this.activeView = this.views.twoFactor
                    }
                    this.isLoading = false
                })
        },

        twoFactorLogin() {
            this.isLoading = true
            this.$store.dispatch(AUTH_REQUEST, {code2fa: this.code2fa, ...this.form})
                .then(() => {
                    window.location = '/'
                    this.isLoading = false
                })
                .catch(error => {
                    if (_.get(error, 'response.status') === 401) {
                        this.twoFactorCodeFieldError = 'Invalid two factor code'
                    } else {
                        this.twoFactorCodeFieldError = 'Unknown error'
                    }
                    this.$bus.$emit('CLEAR_2FA_CODE')
                    this.isLoading = false
                })
        },

        onPasswordInput() {
            this.passwordFieldError = null
            this.vuelidateDelayTouch(this.$v.form.password, this.touchMap)
        },

        onpopstate(event) {
            if (event.state) {
                this.activeView = event.state.tabId
            }
        }
    },
    mounted() {
        // Invite flow redirect
        if (this.$route.params.email && this.$route.params.activeView === this.views.password) {
            this.form.email = this.$route.params.email
            this.activeView = this.$route.params.activeView
        }
        history.pushState({tabId: this.activeView}, null, this.$route.path)
        window.addEventListener('popstate', this.onpopstate)

        if (this.getInviteCode) {
            this.form.inviteCode = this.getInviteCode
        }
    },
    beforeDestroy() {
        // this.$unloadScript("https://edge.xero.com/platform/sso/xero-sso.js")
        window.removeEventListener('popstate', this.onpopstate)
    },
    watch: {
        code2fa(val) {
            this.code2fa = val
            if (val.length === 6) {
                this.twoFactorLogin()
            }
        }
    }
}
</script>

<style scoped src="../../assets/parts/auth-pages.css"></style>
